import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, extractError } from "../../lib/api";
import { formatMoney, formatDate, formatDateTime } from "../../lib/money";
import { useAuth } from "../../contexts/AuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "../../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { ArrowLeft, FileSpreadsheet, MoreHorizontal, History, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ExpenseRowIcons from "../../components/ExpenseRowIcons";

const STATUS_BADGE = {
  matched: "bg-emerald-50 text-emerald-700",
  ambiguous: "bg-amber-50 text-amber-800",
  unmatched: "bg-rose-50 text-rose-700",
  resolved_unknown: "bg-slate-100 text-slate-700",
  non_reimbursable: "bg-slate-200 text-slate-800",
};

function MoneyCell({ cents }) {
  const sign = cents < 0;
  return (
    <span className={`font-mono ${sign ? "text-rose-700" : "text-slate-900"}`}>
      {sign ? "-" : ""}{formatMoney(Math.abs(cents))}
    </span>
  );
}

function MatchedExpenseCell({ exp }) {
  if (!exp) return <span className="text-slate-400">—</span>;
  return (
    <div className="text-xs">
      <div className="flex items-center gap-1.5 font-medium text-slate-700">
        <span>{exp.vendor}</span>
        <ExpenseRowIcons expense={exp} size="sm" />
      </div>
      <div className="text-slate-500">{exp.user_name || "—"} · {exp.category_name || "—"}</div>
    </div>
  );
}

function CandidatesCell({ candidates }) {
  if (!candidates || candidates.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className="bg-amber-50 text-amber-800 hover:bg-amber-50" data-testid="ambiguous-candidates">
            {candidates.length} candidates
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs">
          <div className="space-y-1 text-xs">
            {candidates.map((c) => (
              <div key={c.id}>
                <span className="font-medium">{c.vendor}</span> · {c.user_name || "—"}
                <span className="ml-1 text-slate-400">({formatMoney(c.amount_cents)})</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ----------------------------------------------------------------------
// Action dialogs
// ----------------------------------------------------------------------
function ReasonDialog({ open, onOpenChange, title, description, label, placeholder, onSubmit, submitLabel = "Confirm", destructive = false }) {
  const [val, setVal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setVal(""); }, [open]);
  const go = async () => {
    if (!val.trim()) return;
    setSubmitting(true);
    try { await onSubmit(val.trim()); onOpenChange(false); }
    catch (e) { toast.error(extractError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reason-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">{label}</Label>
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} data-testid="reason-input" autoFocus />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={go} disabled={!val.trim() || submitting} data-testid="reason-submit"
            className={destructive ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90"}>
            {submitting ? "Working…" : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmAmbiguousDialog({ open, onOpenChange, line, onConfirm }) {
  const [picked, setPicked] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setPicked(line?.candidate_expenses?.[0]?.id || ""); }, [open, line]);
  if (!line) return null;
  const go = async () => {
    if (!picked) return;
    setSubmitting(true);
    try { await onConfirm(picked); onOpenChange(false); }
    catch (e) { toast.error(extractError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirm-ambig-dialog">
        <DialogHeader>
          <DialogTitle>Pick the matching expense</DialogTitle>
          <DialogDescription>{line.raw_description} · {formatMoney(line.amount_cents)} · {formatDate(line.occurred_at)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(line.candidate_expenses || []).map((c) => (
            <label key={c.id} className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 ${picked === c.id ? "border-[#0F1E3D] bg-[#0F1E3D]/5" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              data-testid={`pick-cand-${c.id}`}>
              <input type="radio" checked={picked === c.id} onChange={() => setPicked(c.id)} className="mt-1" />
              <div className="text-sm">
                <div className="font-medium">{c.vendor}</div>
                <div className="text-xs text-slate-500">{c.user_name} · {c.category_name} · {formatMoney(c.amount_cents)} · {formatDate(c.occurred_at)}</div>
                {c.comment && <div className="mt-1 text-xs italic text-slate-500">"{c.comment}"</div>}
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={go} disabled={!picked || submitting} data-testid="confirm-pick-btn"
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            {submitting ? "Confirming…" : "Confirm match"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualLinkDialog({ open, onOpenChange, line, batchId, onLinked }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState("");
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) { setQ(""); setItems([]); setPicked(""); setOverride(false); setReason(""); setError(""); return; }
    api.get("/expenses", { params: { card_id: line.card_id, status: "pending", page_size: 50 } })
      .then((r) => setItems(r.data?.items || []))
      .catch((e) => setError(extractError(e)));
  }, [open, line]);

  const filtered = items.filter((e) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (e.vendor || "").toLowerCase().includes(s) || (e.user_name || "").toLowerCase().includes(s);
  });

  const go = async () => {
    if (!picked) return;
    if (override && !reason.trim()) { setError("Reason is required when override is on."); return; }
    setSubmitting(true); setError("");
    try {
      await api.post(`/statements/${batchId}/lines/${line.id}/manual-link`, {
        expense_id: picked, override, override_reason: override ? reason.trim() : null,
      });
      toast.success("Linked");
      onOpenChange(false);
      onLinked?.();
    } catch (e) { setError(extractError(e)); }
    finally { setSubmitting(false); }
  };
  if (!line) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manual-link-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manual link</DialogTitle>
          <DialogDescription>
            Link this line to any pending expense on the same card. Date constraint is relaxed; amount must match unless override is on.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendor or worker…" data-testid="manual-link-search" />
          <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">No pending expenses on this card.</div>
            ) : filtered.map((e) => (
              <label key={e.id} className={`flex cursor-pointer items-center gap-2 border-b border-slate-100 p-2 text-sm ${picked === e.id ? "bg-[#0F1E3D]/5" : "hover:bg-slate-50"}`}
                data-testid={`manual-link-row-${e.id}`}>
                <input type="radio" checked={picked === e.id} onChange={() => setPicked(e.id)} />
                <span className="font-medium">{e.vendor}</span>
                <span className="font-mono text-xs">{formatMoney(e.amount_cents)}</span>
                <span className="text-xs text-slate-500">· {e.user_name} · {formatDate(e.occurred_at)}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox checked={override} onCheckedChange={(v) => setOverride(Boolean(v))} id="override-toggle" data-testid="override-toggle" />
          <Label htmlFor="override-toggle" className="text-sm font-normal">Override amount mismatch</Label>
        </div>
        {override && (
          <div className="space-y-1">
            <Label className="text-xs">Override reason (required)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. tip included on receipt" data-testid="override-reason" />
          </div>
        )}
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={go} disabled={!picked || submitting} data-testid="manual-link-submit"
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            {submitting ? "Linking…" : "Link expense"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RequestCommentDialog({ open, onOpenChange, line, batchId, onCreated }) {
  const [workers, setWorkers] = useState([]);
  const [selected, setSelected] = useState({});
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setMessage(""); setError("");
    api.get(`/cards/available`).then((r) => {
      // Default: workers assigned to this card
      api.get("/users/workers").then((u) => {
        const allWorkers = (u.data || []).filter((x) => x.role === "worker" && x.active);
        setWorkers(allWorkers);
        const card = (r.data || []).find((c) => c.id === line.card_id);
        const defaultSet = {};
        const cardWorkerIds = new Set((card?.assigned_user_ids) || []);
        allWorkers.forEach((w) => { defaultSet[w.id] = cardWorkerIds.has(w.id); });
        setSelected(defaultSet);
      });
    });
  }, [open, line]);

  const go = async () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) { setError("Select at least one worker."); return; }
    setSubmitting(true); setError("");
    try {
      await api.post(`/statements/${batchId}/lines/${line.id}/request-comment`, {
        assignee_user_ids: ids, message: message.trim() || null,
      });
      toast.success("Comment request sent");
      onOpenChange(false);
      onCreated?.();
    } catch (e) { setError(extractError(e)); }
    finally { setSubmitting(false); }
  };

  if (!line) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="request-comment-dialog">
        <DialogHeader>
          <DialogTitle>Ask workers for a comment</DialogTitle>
          <DialogDescription>{line.raw_description} · {formatMoney(line.amount_cents)} · {formatDate(line.occurred_at)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Assign to</Label>
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
            {workers.map((w) => (
              <label key={w.id} className="flex items-center gap-2 text-sm" data-testid={`chase-assignee-${w.id}`}>
                <Checkbox checked={!!selected[w.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [w.id]: Boolean(v) }))} />
                <span>{w.name}</span><span className="text-xs text-slate-400">{w.email}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Message (optional)</Label>
          <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Anything you want to say…" data-testid="chase-message" />
        </div>
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={go} disabled={submitting} data-testid="chase-submit"
            className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            {submitting ? "Sending…" : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------
// Action menu per line
// ----------------------------------------------------------------------
function LineActions({ line, batchId, onChange }) {
  const [confirmAmbigOpen, setConfirmAmbigOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [chaseOpen, setChaseOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(null); // 'reject' | 'nonreimb' | 'resolved'

  const post = async (path, body) => {
    await api.post(`/statements/${batchId}/lines/${line.id}${path}`, body);
    onChange?.();
  };

  const handleConfirm = async () => {
    if (line.match_status === "ambiguous" && (line.candidate_expenses || []).length > 1) {
      setConfirmAmbigOpen(true);
    } else if (line.matched_expense_id) {
      try { await post(`/confirm-match`, { expense_id: line.matched_expense_id }); toast.success("Match confirmed"); }
      catch (e) { toast.error(extractError(e)); }
    } else if ((line.candidate_expenses || []).length === 1) {
      try { await post(`/confirm-match`, { expense_id: line.candidate_expenses[0].id }); toast.success("Match confirmed"); }
      catch (e) { toast.error(extractError(e)); }
    } else {
      setManualOpen(true);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`row-actions-${line.id}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs text-slate-500">Actions</DropdownMenuLabel>
          {(line.match_status === "matched" || line.match_status === "ambiguous" || line.match_status === "unmatched") && (
            <>
              {(line.match_status === "ambiguous" || line.match_status === "unmatched") && (
                <DropdownMenuItem onClick={handleConfirm} data-testid="act-confirm">Confirm match</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setManualOpen(true)} data-testid="act-manual-link">Manual link…</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setChaseOpen(true)} data-testid="act-request-comment">Request comment…</DropdownMenuItem>
              <DropdownMenuSeparator />
              {line.match_status !== "unmatched" && (
                <DropdownMenuItem onClick={() => setReasonOpen("reject")} className="text-rose-600" data-testid="act-reject">
                  Reject / undo match
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setReasonOpen("nonreimb")} data-testid="act-nonreimb">
                Mark non-reimbursable
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setReasonOpen("resolved")} data-testid="act-resolved">
                Mark resolved (unknown)
              </DropdownMenuItem>
            </>
          )}
          {(line.match_status === "non_reimbursable" || line.match_status === "resolved_unknown") && (
            <DropdownMenuItem onClick={() => setReasonOpen("reject")} className="text-rose-600" data-testid="act-undo">
              Undo (back to unmatched)
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmAmbiguousDialog open={confirmAmbigOpen} onOpenChange={setConfirmAmbigOpen} line={line}
        onConfirm={async (expense_id) => { await post(`/confirm-match`, { expense_id }); toast.success("Match confirmed"); }} />
      <ManualLinkDialog open={manualOpen} onOpenChange={setManualOpen} line={line} batchId={batchId}
        onLinked={() => onChange?.()} />
      <RequestCommentDialog open={chaseOpen} onOpenChange={setChaseOpen} line={line} batchId={batchId}
        onCreated={() => onChange?.()} />
      <ReasonDialog open={reasonOpen === "reject"} onOpenChange={() => setReasonOpen(null)}
        title="Reject / undo match" description="The line returns to unmatched and any linked expense is released back to pending."
        label="Reason (optional)" placeholder="e.g. wrong worker / fraudulent" destructive
        submitLabel="Reject" onSubmit={async (reason) => { await post(`/reject-match`, { reason }); toast.success("Reverted"); }} />
      <ReasonDialog open={reasonOpen === "nonreimb"} onOpenChange={() => setReasonOpen(null)}
        title="Mark non-reimbursable" description="Use for company-paid charges that aren't a worker swipe (fees, monthly statements, etc.)."
        label="Reason" placeholder="e.g. monthly card fee"
        submitLabel="Mark" onSubmit={async (reason) => { await post(`/mark-non-reimbursable`, { reason }); toast.success("Marked non-reimbursable"); }} />
      <ReasonDialog open={reasonOpen === "resolved"} onOpenChange={() => setReasonOpen(null)}
        title="Mark resolved (unknown)" description="Acknowledged but no matching expense exists (e.g. refunds, transfers)."
        label="Note" placeholder="e.g. refund from prior statement"
        submitLabel="Mark" onSubmit={async (note) => { await post(`/mark-resolved-unknown`, { note }); toast.success("Resolved"); }} />
    </>
  );
}

// ----------------------------------------------------------------------
// Lines table with active-row selection + keyboard shortcuts
// ----------------------------------------------------------------------
function LinesTable({ batchId, filter, refreshKey, onActionDone }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { page_size: 200 };
      if (filter?.match_status?.length) params.match_status = filter.match_status;
      const r = await api.get(`/statements/${batchId}/lines`, { params });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setActive(0);
    } catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, [batchId, filter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = async (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (items.length === 0) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(items.length - 1, i + 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
      else if (["c", "r", "n", "u"].includes(e.key.toLowerCase())) {
        e.preventDefault();
        const line = items[active]; if (!line) return;
        try {
          if (e.key.toLowerCase() === "c" && line.candidate_expenses?.length === 1) {
            await api.post(`/statements/${batchId}/lines/${line.id}/confirm-match`, { expense_id: line.candidate_expenses[0].id });
            toast.success("Match confirmed"); onActionDone?.();
          } else if (e.key.toLowerCase() === "r" && line.match_status !== "unmatched") {
            await api.post(`/statements/${batchId}/lines/${line.id}/reject-match`, { reason: "keyboard shortcut" });
            toast.success("Reverted"); onActionDone?.();
          } else if (e.key.toLowerCase() === "n") {
            await api.post(`/statements/${batchId}/lines/${line.id}/mark-non-reimbursable`, { reason: "marked via keyboard" });
            toast.success("Non-reimbursable"); onActionDone?.();
          } else if (e.key.toLowerCase() === "u") {
            await api.post(`/statements/${batchId}/lines/${line.id}/mark-resolved-unknown`, { note: "resolved via keyboard" });
            toast.success("Resolved unknown"); onActionDone?.();
          }
        } catch (err) { toast.error(extractError(err)); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [items, active, batchId, onActionDone]);

  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;

  return (
    <div>
      <p className="mb-2 text-xs text-slate-500">{total} line{total === 1 ? "" : "s"}</p>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <Table data-testid="lines-table">
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Match / Candidates</TableHead>
              <TableHead className="text-xs">Ref</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="py-6 text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-slate-500">No lines in this view.</TableCell></TableRow>
            ) : items.map((ln, idx) => (
              <TableRow key={ln.id} data-testid={`line-row-${ln.id}`}
                className={idx === active ? "bg-[#0F1E3D]/5" : ""}
                onClick={() => setActive(idx)}>
                <TableCell className="whitespace-nowrap text-xs text-slate-600">{formatDate(ln.occurred_at)}</TableCell>
                <TableCell className="text-sm">{ln.raw_description}</TableCell>
                <TableCell className="text-right"><MoneyCell cents={ln.amount_cents} /></TableCell>
                <TableCell>
                  <Badge className={`${STATUS_BADGE[ln.match_status] || "bg-slate-100 text-slate-700"} hover:opacity-90`}
                    data-testid={`status-${ln.match_status}`}>{ln.match_status}</Badge>
                </TableCell>
                <TableCell>
                  {ln.match_status === "matched" ? <MatchedExpenseCell exp={ln.matched_expense} />
                    : ln.match_status === "ambiguous" ? <CandidatesCell candidates={ln.candidate_expenses} />
                    : <span className="text-slate-400 text-xs">—</span>}
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{ln.external_ref || "—"}</TableCell>
                <TableCell><LineActions line={ln} batchId={batchId} onChange={onActionDone} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-2 text-xs text-slate-500" data-testid="kbd-cheatsheet">
        Keyboard: <kbd className="rounded border bg-slate-50 px-1.5 font-mono text-[10px]">↑↓</kbd> navigate ·
        <kbd className="ml-1 rounded border bg-slate-50 px-1.5 font-mono text-[10px]">C</kbd> confirm (single candidate) ·
        <kbd className="ml-1 rounded border bg-slate-50 px-1.5 font-mono text-[10px]">R</kbd> reject ·
        <kbd className="ml-1 rounded border bg-slate-50 px-1.5 font-mono text-[10px]">N</kbd> non-reimbursable ·
        <kbd className="ml-1 rounded border bg-slate-50 px-1.5 font-mono text-[10px]">U</kbd> resolved-unknown
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Audit drawer
// ----------------------------------------------------------------------
function AuditDrawer({ open, onOpenChange, batchId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/statements/${batchId}/audit`, { params: { page_size: 100 } })
      .then((r) => setItems(r.data?.items || []))
      .finally(() => setLoading(false));
  }, [open, batchId, refreshKey]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="audit-drawer">
      <div className="flex-1 bg-black/30" onClick={() => onOpenChange(false)} />
      <aside className="w-full max-w-md overflow-y-auto bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <History className="h-5 w-5" /> Activity
          </h2>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} data-testid="close-audit-drawer">
            <X className="h-5 w-5" />
          </Button>
        </div>
        {loading ? (
          <div className="text-center text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-slate-500">No activity yet on this batch.</div>
        ) : (
          <ol className="space-y-2">
            {items.map((e) => (
              <li key={e.id} className="rounded-md border border-slate-200 bg-white p-3 text-xs" data-testid={`audit-entry-${e.id}`}>
                <div className="flex justify-between text-[10px] uppercase tracking-wide text-slate-500">
                  <span>{e.action}</span>
                  <span>{formatDateTime(e.created_at)}</span>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-700">{e.actor_name || e.actor_user_id}</div>
                <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11px] text-slate-600">
                  {JSON.stringify(e.payload_summary, null, 1)}
                </pre>
              </li>
            ))}
          </ol>
        )}
      </aside>
    </div>
  );
}

// ----------------------------------------------------------------------
// Main page
// ----------------------------------------------------------------------
export default function StatementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshCounters } = useAuth();
  const isAdmin = user?.role === "admin";
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [auditOpen, setAuditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/statements/${id}`);
      setBatch(r.data);
      setRefreshKey((k) => k + 1);
      refreshCounters?.();
    } catch (e) { setError(extractError(e)); }
  }, [id, refreshCounters]);

  useEffect(() => {
    setLoading(true); setError("");
    api.get(`/statements/${id}`).then((r) => setBatch(r.data)).catch((e) => setError(extractError(e))).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-10 text-center text-slate-500">Loading…</div>;
  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;
  if (!batch) return null;

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const r = await api.delete(`/statements/${id}`);
      toast.success(
        `Batch deleted · ${r.data.lines_deleted} lines, ${r.data.expenses_reverted} expenses reverted, ${r.data.chases_cancelled} chase(s) cancelled`
      );
      navigate("/statements", { replace: true });
    } catch (e) {
      toast.error(extractError(e));
      setDeleting(false);
    }
  };

  return (
    <div data-testid="statement-detail">
      <div className="mb-4 flex items-center justify-between">
        <Link to="/statements" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0F1E3D]" data-testid="back-to-statements">
          <ArrowLeft className="h-4 w-4" /> Back to statements
        </Link>
        <Button variant="outline" size="sm" onClick={() => setAuditOpen(true)} data-testid="open-audit-drawer">
          <History className="mr-1 h-4 w-4" /> Activity
        </Button>
        {isAdmin && (
          <Button
            variant="outline" size="sm"
            onClick={() => setDeleteOpen(true)}
            data-testid="batch-delete-btn"
            className="ml-2 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800">
            <Trash2 className="mr-1 h-4 w-4" /> Delete batch
          </Button>
        )}
      </div>
      <div className="mb-6 rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[#0F1E3D]" />
              <h1 className="text-xl font-semibold tracking-tight text-slate-900" data-testid="batch-filename">{batch.original_filename}</h1>
            </div>
            <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-slate-500 sm:grid-cols-2">
              <div><span className="font-medium text-slate-600">Card:</span> {batch.card_label} · <span className="font-mono">{batch.card_last4}</span></div>
              <div><span className="font-medium text-slate-600">Uploaded:</span> {formatDateTime(batch.uploaded_at)} by {batch.uploaded_by_name || batch.uploaded_by_email}</div>
              <div><span className="font-medium text-slate-600">Period:</span> {batch.period_start ? `${formatDate(batch.period_start)} – ${formatDate(batch.period_end)}` : "—"}</div>
              <div><span className="font-medium text-slate-600">Status:</span> <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">{batch.status}</Badge></div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700">Matched</div>
              <div className="text-lg font-semibold text-emerald-800" data-testid="count-matched">{batch.matched_count}</div>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-amber-800">Ambiguous</div>
              <div className="text-lg font-semibold text-amber-900" data-testid="count-ambiguous">{batch.ambiguous_count}</div>
            </div>
            <div className="rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-rose-700">Unmatched</div>
              <div className="text-lg font-semibold text-rose-800" data-testid="count-unmatched">{batch.unmatched_count}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Total</div>
              <div className="text-lg font-semibold text-slate-700" data-testid="count-total">{batch.total_lines}</div>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList data-testid="lines-tabs">
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="matched" data-testid="tab-matched">Matched</TabsTrigger>
          <TabsTrigger value="ambiguous" data-testid="tab-ambiguous">Ambiguous</TabsTrigger>
          <TabsTrigger value="unmatched" data-testid="tab-unmatched">Unmatched</TabsTrigger>
          <TabsTrigger value="nonreimb" data-testid="tab-nonreimb">Non-reimbursable</TabsTrigger>
        </TabsList>
        <TabsContent value="all" className="mt-4"><LinesTable batchId={id} filter={null} refreshKey={refreshKey} onActionDone={reload} /></TabsContent>
        <TabsContent value="matched" className="mt-4"><LinesTable batchId={id} filter={{ match_status: ["matched"] }} refreshKey={refreshKey} onActionDone={reload} /></TabsContent>
        <TabsContent value="ambiguous" className="mt-4"><LinesTable batchId={id} filter={{ match_status: ["ambiguous"] }} refreshKey={refreshKey} onActionDone={reload} /></TabsContent>
        <TabsContent value="unmatched" className="mt-4"><LinesTable batchId={id} filter={{ match_status: ["unmatched"] }} refreshKey={refreshKey} onActionDone={reload} /></TabsContent>
        <TabsContent value="nonreimb" className="mt-4"><LinesTable batchId={id} filter={{ match_status: ["non_reimbursable"] }} refreshKey={refreshKey} onActionDone={reload} /></TabsContent>
      </Tabs>

      <AuditDrawer open={auditOpen} onOpenChange={setAuditOpen} batchId={id} refreshKey={refreshKey} />

      <Dialog open={deleteOpen} onOpenChange={(v) => !v && !deleting && setDeleteOpen(false)}>
        <DialogContent data-testid="batch-delete-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete batch?</DialogTitle>
            <DialogDescription>
              <span className="font-mono">{batch.original_filename}</span> — this will delete{" "}
              <strong>{batch.total_lines}</strong> line(s), revert{" "}
              <strong>{batch.matched_count}</strong> matched expense(s) to pending, and cancel any
              open chases tied to this batch. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="batch-delete-confirm-btn"
              className="bg-rose-600 text-white hover:bg-rose-700">
              {deleting ? "Deleting…" : "Delete batch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
